// SCR-STAFF-102 — تب خانواده (AP-PAT-02): ولی قانونی با نوع اختیار و اعتبار (US-PAT-040؛ کودک بدون ولی معتبر نشان «ولی ثبت نشده»)،
// همراه با سطح دسترسی (US-PAT-041؛ دسترسی فقط با رضایت اشتراک با همراه — PHX-PAT-0503)، پایان پیوند با دلیل، و پرداخت‌کننده
// پیش‌فرض صورتحساب (US-PAT-043؛ خود، فرد دیگر یا سازمان). AP-PAT-03: حساب خانوادگی (US-PAT-042).
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type DefaultPayerRequest,
  type GuardianAuthority,
  type PatientSearchItem,
  type PatientView,
  type PayerKind,
  type RelationRequest,
  type RelationshipKind,
  type RelationType,
  type RelationView,
} from '@phoenix/api-client';
import { useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, FormDialog, ReasonDialog, toJalali, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { formatJalaliInput, parseJalaliInput, patKeys, usePatientInvalidation } from './common';
import { FamilyAccountSection } from './FamilyAccountSection';
import { PatientPicker } from './PatientPicker';

const RELATIONSHIPS: RelationshipKind[] = ['Father', 'Mother', 'Guardian', 'Spouse', 'Child', 'Sibling', 'Other'];
const AUTHORITIES: NonNullable<GuardianAuthority>[] = ['Custody', 'Guardianship', 'PowerOfAttorney'];
export const COMPANION_ACCESS = ['Notifications', 'ManageAppointments', 'ViewBilling', 'ViewResults'] as const;

function useRelations(patientId: string) {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: patKeys.relations(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/patients/patients/{id}/relations', { params: { path: { id: patientId } } })),
  });
}

function AccessChecks({ value, onChange }: { value: string[]; onChange: (value: string[]) => void }) {
  const { t } = useTranslation('pat');
  return (
    <FormGroup>
      <FormLabel component="legend">{t('pat:relations.access_label')}</FormLabel>
      {COMPANION_ACCESS.map((flag) => (
        <FormControlLabel
          key={flag}
          control={
            <Checkbox
              checked={value.includes(flag)}
              onChange={(event) => onChange(event.target.checked ? [...value, flag] : value.filter((item) => item !== flag))}
            />
          }
          label={t(`pat:relations.access.${flag}`)}
        />
      ))}
    </FormGroup>
  );
}

function AddRelationDialog({ patientId, open, onClose }: { patientId: string; open: boolean; onClose: () => void }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const invalidate = usePatientInvalidation(patientId);
  const idempotency = useIdempotencyKey();
  const [type, setType] = useState<RelationType>('Guardian');
  const [related, setRelated] = useState<PatientSearchItem | null>(null);
  const [relationship, setRelationship] = useState<RelationshipKind>('Father');
  const [authority, setAuthority] = useState<NonNullable<GuardianAuthority>>('Custody');
  const [validTo, setValidTo] = useState('');
  const [access, setAccess] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);
  useEffect(() => {
    if (open) {
      setType('Guardian');
      setRelated(null);
      setRelationship('Father');
      setAuthority('Custody');
      setValidTo('');
      setAccess([]);
      setTouched(false);
    }
  }, [open]);
  const save = useMutation({
    mutationFn: (body: RelationRequest) =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients/{id}/relations', {
          params: { path: { id: patientId } },
          body,
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (data) => {
      queryClient.setQueryData(patKeys.relations(session?.tenantId, patientId), data);
      invalidate();
      onClose();
    },
  });
  const validToInvalid = validTo.trim().length > 0 && !parseJalaliInput(validTo);
  const submit = () => {
    setTouched(true);
    if (!related || validToInvalid) {
      return;
    }
    save.mutate({
      relatedPatientId: related.id,
      type,
      relationship,
      authority: type === 'Guardian' ? authority : null,
      access: type === 'Companion' ? access : null,
      validTo: parseJalaliInput(validTo) ?? null,
    });
  };
  return (
    <FormDialog
      open={open}
      titleKey="pat:relations.add"
      screenId="SCR-STAFF-102-RELATION"
      onSubmit={submit}
      onClose={onClose}
      isDirty={related !== null}
      isSubmitting={save.isPending}
      submitError={save.error}
    >
      <TextField select label={t('pat:relations.type_label')} value={type} onChange={(event) => setType(event.target.value as RelationType)} fullWidth>
        {(['Guardian', 'Companion'] as const).map((value) => (
          <MenuItem key={value} value={value}>
            {t(`pat:relations.types.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <PatientPicker
        labelKey="pat:relations.related"
        value={related}
        onChange={setRelated}
        excludeIds={[patientId]}
        required
        error={touched && !related ? 'common:validation.required' : undefined}
        testId="relation-patient"
      />
      <TextField
        select
        label={t('pat:relations.relationship_label')}
        value={relationship}
        onChange={(event) => setRelationship(event.target.value as RelationshipKind)}
        fullWidth
      >
        {RELATIONSHIPS.map((value) => (
          <MenuItem key={value} value={value}>
            {t(`pat:relations.relationship.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      {type === 'Guardian' ? (
        <>
          <TextField
            select
            label={t('pat:relations.authority_label')}
            value={authority}
            onChange={(event) => setAuthority(event.target.value as NonNullable<GuardianAuthority>)}
            fullWidth
          >
            {AUTHORITIES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`pat:relations.authority.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('pat:relations.valid_to')}
            helperText={touched && validToInvalid ? t('pat:validation.birth_date') : t('pat:relations.valid_to_help')}
            error={touched && validToInvalid}
            value={validTo}
            onChange={(event) => setValidTo(event.target.value)}
            fullWidth
            slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }}
          />
        </>
      ) : (
        <AccessChecks value={access} onChange={setAccess} />
      )}
    </FormDialog>
  );
}

function EditAccessDialog({ patientId, relation, onClose }: { patientId: string; relation: RelationView | null; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [access, setAccess] = useState<string[]>([]);
  const [validTo, setValidTo] = useState('');
  useEffect(() => {
    setAccess(relation?.access ?? []);
    setValidTo(formatJalaliInput(relation?.validTo));
  }, [relation]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/patients/patients/{id}/relations/{relationId}', {
          params: { path: { id: patientId, relationId: relation?.id ?? '' } },
          body: { access: relation?.type === 'Companion' ? access : null, validTo: parseJalaliInput(validTo) ?? null },
        }),
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(patKeys.relations(session?.tenantId, patientId), data);
      onClose();
    },
  });
  const { t } = useTranslation('pat');
  return (
    <FormDialog
      open={relation !== null}
      titleKey="pat:relations.edit"
      screenId="SCR-STAFF-102-RELATION-EDIT"
      onSubmit={() => save.mutate()}
      onClose={onClose}
      isDirty
      isSubmitting={save.isPending}
      submitError={save.error}
    >
      {relation?.type === 'Companion' && <AccessChecks value={access} onChange={setAccess} />}
      <TextField
        label={t('pat:relations.valid_to')}
        helperText={t('pat:relations.valid_to_help')}
        value={validTo}
        onChange={(event) => setValidTo(event.target.value)}
        fullWidth
        slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric' } }}
      />
    </FormDialog>
  );
}

function RelationsSection({ patient, canManage }: { patient: PatientView; canManage: boolean }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const invalidate = usePatientInvalidation(patient.id);
  const relations = useRelations(patient.id);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<RelationView | null>(null);
  const [ending, setEnding] = useState<RelationView | null>(null);
  const end = useMutation({
    mutationFn: ({ relation, reason }: { relation: RelationView; reason: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/patients/patients/{id}/relations/{relationId}:end', {
          params: { path: { id: patient.id, relationId: relation.id } },
          body: { reason },
        }),
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(patKeys.relations(session?.tenantId, patient.id), data);
      invalidate();
      setEnding(null);
    },
  });
  const columns: DataTableColumn<RelationView>[] = [
    {
      id: 'type',
      headerKey: 'pat:relations.type_label',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>{t(`pat:relations.types.${row.type}`)}</span>
          {!row.isActive && <Chip size="small" label={t('pat:relations.ended')} />}
        </Stack>
      ),
    },
    {
      id: 'related',
      headerKey: 'pat:relations.related',
      cell: (row) => (
        <span>
          {row.relatedDisplayName}
          {row.relatedFileNumber ? ` (${faDigits(row.relatedFileNumber)})` : ''}
        </span>
      ),
    },
    { id: 'relationship', headerKey: 'pat:relations.relationship_label', cell: (row) => t(`pat:relations.relationship.${row.relationship}`) },
    {
      id: 'detail',
      headerKey: 'pat:relations.detail',
      cell: (row) =>
        row.type === 'Guardian'
          ? row.authority
            ? t(`pat:relations.authority.${row.authority}`)
            : null
          : row.access.length > 0
            ? row.access.map((flag) => t(`pat:relations.access.${flag}`)).join(t('pat:list_separator'))
            : t('pat:relations.no_access'),
    },
    {
      id: 'validity',
      headerKey: 'pat:relations.validity',
      cell: (row) =>
        row.endedAt
          ? t(`pat:relations.end_reason.${row.endReason ?? 'Ended'}`)
          : `${toJalali(row.validFrom)}${row.validTo ? ` – ${toJalali(row.validTo)}` : ''}`,
    },
    {
      id: 'actions',
      headerKey: 'pat:relations.edit',
      cell: (row) =>
        canManage && row.isActive && row.direction === 'Outgoing' ? (
          <Stack direction="row" spacing={0.5}>
            <IconButton aria-label={t('pat:relations.edit')} onClick={() => setEditing(row)}>
              <EditOutlinedIcon />
            </IconButton>
            <IconButton aria-label={t('pat:relations.end')} onClick={() => setEnding(row)}>
              <LinkOffOutlinedIcon />
            </IconButton>
          </Stack>
        ) : null,
    },
  ];
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h2" sx={{ flexGrow: 1 }}>
          {t('pat:relations.title')}
        </Typography>
        {canManage && (
          <Button startIcon={<AddIcon />} onClick={() => setAdding(true)} data-testid="relation-add">
            {t('pat:relations.add')}
          </Button>
        )}
      </Stack>
      {relations.data?.guardianMissing && (
        <Alert severity="warning" sx={{ mb: 1 }} data-testid="guardian-missing-alert">
          {t('pat:relations.guardian_missing')}
        </Alert>
      )}
      <DataTable
        captionKey="pat:relations.title"
        columns={columns}
        rows={relations.data?.items}
        isLoading={relations.isPending}
        error={relations.error}
        onRetry={() => void relations.refetch()}
        getRowId={(row) => row.id}
        emptyTitleKey="pat:relations.empty"
      />
      <AddRelationDialog patientId={patient.id} open={adding} onClose={() => setAdding(false)} />
      <EditAccessDialog patientId={patient.id} relation={editing} onClose={() => setEditing(null)} />
      <ReasonDialog
        open={ending !== null}
        titleKey="pat:relations.end"
        messageKey="pat:relations.end_confirm"
        busy={end.isPending}
        error={end.error}
        onConfirm={(reason) => ending && end.mutate({ relation: ending, reason })}
        onCancel={() => setEnding(null)}
      />
    </Paper>
  );
}

function DefaultPayerSection({ patient, canWrite }: { patient: PatientView; canWrite: boolean }) {
  const { t } = useTranslation('pat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const current = patient.defaultPayer;
  const [kind, setKind] = useState<PayerKind>(current.kind);
  const [payer, setPayer] = useState<PatientSearchItem | null>(null);
  const [organization, setOrganization] = useState(current.organizationName ?? '');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setKind(current.kind);
    setOrganization(current.organizationName ?? '');
  }, [current.kind, current.organizationName]);
  const save = useMutation({
    mutationFn: (body: DefaultPayerRequest) =>
      unwrap(tenantApi.PUT('/api/v1/patients/patients/{id}/default-payer', { params: { path: { id: patient.id } }, body })),
    onMutate: () => setSaved(false),
    onSuccess: (updated) => {
      queryClient.setQueryData(patKeys.patient(session?.tenantId, patient.id), updated);
      setPayer(null);
      setSaved(true);
    },
  });
  const invalid = (kind === 'Person' && !payer && current.kind !== 'Person') || (kind === 'Organization' && !organization.trim());
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="default-payer">
      <Typography variant="h2" gutterBottom>
        {t('pat:payer.title')}
      </Typography>
      <Stack spacing={2}>
        {saved && <Alert severity="success">{t('pat:payer.saved')}</Alert>}
        <Typography variant="body2" color="text.secondary">
          {t('pat:payer.current', {
            payer:
              current.kind === 'Self'
                ? t('pat:payer.kinds.Self')
                : current.kind === 'Person'
                  ? (current.payerDisplayName ?? '')
                  : (current.organizationName ?? ''),
          })}
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            select
            label={t('pat:payer.kind_label')}
            value={kind}
            onChange={(event) => setKind(event.target.value as PayerKind)}
            disabled={!canWrite}
            sx={{ minWidth: 200 }}
          >
            {(['Self', 'Person', 'Organization'] as const).map((value) => (
              <MenuItem key={value} value={value}>
                {t(`pat:payer.kinds.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          {kind === 'Person' && canWrite && (
            <PatientPicker labelKey="pat:payer.person" value={payer} onChange={setPayer} excludeIds={[patient.id]} testId="payer-patient" />
          )}
          {kind === 'Organization' && (
            <TextField
              label={t('pat:payer.organization')}
              value={organization}
              onChange={(event) => setOrganization(event.target.value)}
              disabled={!canWrite}
              fullWidth
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
          )}
        </Stack>
        {save.error ? <ErrorState error={save.error} compact /> : null}
        {canWrite && (
          <div>
            <Button
              variant="contained"
              disabled={save.isPending || invalid}
              onClick={() =>
                save.mutate({
                  kind,
                  payerPatientId: kind === 'Person' ? (payer?.id ?? current.payerPatientId) : null,
                  organizationName: kind === 'Organization' ? organization.trim() : null,
                })
              }
              data-testid="payer-save"
            >
              {t('common:actions.save')}
            </Button>
          </div>
        )}
      </Stack>
    </Paper>
  );
}

export function PatientFamilyPanel({ patient, canManage, canWrite }: { patient: PatientView; canManage: boolean; canWrite: boolean }) {
  return (
    <Stack spacing={2}>
      <RelationsSection patient={patient} canManage={canManage} />
      <FamilyAccountSection patient={patient} canWrite={canWrite} />
      <DefaultPayerSection patient={patient} canWrite={canWrite} />
    </Stack>
  );
}
